<template>
  <div class="pos-controle-page">
    <b-card class="mb-5 border-0 shadow-sm">
      <div class="d-flex flex-wrap justify-content-between align-items-start">
        <div>
          <h3 class="font-weight-bolder text-dark mb-2">
            Integração POS Controle
          </h3>
          <p class="text-muted mb-0">
            Conecte sua conta, envie os produtos e acompanhe as vendas do POS.
          </p>
        </div>
        <div class="d-flex align-items-center mt-2 mt-md-0">
          <span class="badge mr-3" :class="statusConexaoClass">
            {{ statusConexaoLabel }}
          </span>
          <router-link :to="{ name: 'posLista' }" class="btn btn-light">
            Voltar aos terminais
          </router-link>
        </div>
      </div>
    </b-card>

    <b-card class="border-0 shadow-sm">
      <b-tabs
        v-model="abaAtiva"
        pills
        card
        fill
        class="pos-controle-tabs"
        nav-class="pos-controle-tabs-nav"
        @input="alterarAba"
      >
        <b-tab title="Conexão">
          <b-overlay :show="carregandoConfiguracao" rounded="sm">
            <b-alert v-if="integracao.ultimo_erro" show variant="danger">
              {{ integracao.ultimo_erro }}
            </b-alert>

            <div
              class="d-flex flex-wrap justify-content-between mb-4 text-muted"
            >
              <span>Credenciais {{ origemCredenciaisLabel }}.</span>
              <span v-if="integracao.ultima_tentativa_em">
                Última tentativa:
                {{ formatarData(integracao.ultima_tentativa_em) }}
              </span>
            </div>

            <b-form @submit.prevent="salvarConfiguracao">
              <b-form-checkbox v-model="configForm.ativo" switch class="mb-4">
                Integração ativa
              </b-form-checkbox>

              <details class="pos-section mb-4" :open="!configurado">
                <summary>Credenciais de acesso</summary>
                <div class="p-4">
                  <p class="text-muted">
                    Preencha somente os dados que deseja alterar.
                  </p>
                  <b-row>
                    <b-col md="4">
                      <b-form-group
                        label="Usuário da API"
                        label-for="pos-controle-usuario"
                        description="Preencha apenas para cadastrar ou alterar o usuário. Deixe vazio para manter o salvo."
                      >
                        <b-form-input
                          id="pos-controle-usuario"
                          v-model.trim="configForm.username"
                          maxlength="255"
                          autocomplete="off"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group
                        label="Senha da API"
                        label-for="pos-controle-senha"
                        :description="descricaoSenha"
                      >
                        <b-input-group>
                          <b-form-input
                            id="pos-controle-senha"
                            v-model="configForm.password"
                            :type="mostrarSenha ? 'text' : 'password'"
                            maxlength="255"
                            autocomplete="new-password"
                          />
                          <b-input-group-append>
                            <b-button
                              type="button"
                              variant="outline-secondary"
                              :title="
                                mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'
                              "
                              :aria-label="
                                mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'
                              "
                              @click="mostrarSenha = !mostrarSenha"
                            >
                              <i
                                :class="
                                  mostrarSenha
                                    ? 'fas fa-eye-slash'
                                    : 'fas fa-eye'
                                "
                                aria-hidden="true"
                              ></i>
                            </b-button>
                          </b-input-group-append>
                        </b-input-group>
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group
                        :label="possuiChaveOcp ? 'CHAVE OCP' : 'CHAVE OCP *'"
                        label-for="pos-controle-chave-ocp"
                        :description="descricaoChaveOcp"
                      >
                        <b-input-group>
                          <b-form-input
                            id="pos-controle-chave-ocp"
                            v-model.trim="configForm.chave_ocp"
                            :type="mostrarChaveOcp ? 'text' : 'password'"
                            :required="!possuiChaveOcp"
                            maxlength="255"
                            autocomplete="new-password"
                          />
                          <b-input-group-append>
                            <b-button
                              type="button"
                              variant="outline-secondary"
                              :title="
                                mostrarChaveOcp
                                  ? 'Ocultar CHAVE OCP'
                                  : 'Mostrar CHAVE OCP'
                              "
                              :aria-label="
                                mostrarChaveOcp
                                  ? 'Ocultar CHAVE OCP'
                                  : 'Mostrar CHAVE OCP'
                              "
                              @click="mostrarChaveOcp = !mostrarChaveOcp"
                            >
                              <i
                                :class="
                                  mostrarChaveOcp
                                    ? 'fas fa-eye-slash'
                                    : 'fas fa-eye'
                                "
                                aria-hidden="true"
                              ></i>
                            </b-button>
                          </b-input-group-append>
                        </b-input-group>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </div>
              </details>

              <details class="pos-section">
                <summary>Automações e tratamento das vendas</summary>
                <div class="automacoes-grid">
                  <section
                    v-for="painel in automacoes"
                    :key="painel.titulo"
                    class="automacoes-panel"
                  >
                    <div class="automacoes-heading">
                      <span class="automacoes-icon" aria-hidden="true"
                        ><i :class="painel.icone"></i
                      ></span>
                      <div>
                        <h6>{{ painel.titulo }}</h6>
                        <p>{{ painel.descricao }}</p>
                      </div>
                    </div>
                    <div
                      v-for="opcao in painel.opcoes"
                      :key="opcao.campo"
                      class="automacao-option"
                    >
                      <div class="automacao-text">
                        <label :for="'pos-auto-' + opcao.campo">{{
                          opcao.titulo
                        }}</label>
                        <p :id="'pos-auto-ajuda-' + opcao.campo">
                          {{ opcao.descricao }}
                        </p>
                      </div>
                      <b-form-checkbox
                        :id="'pos-auto-' + opcao.campo"
                        v-model="configForm[opcao.campo]"
                        :aria-describedby="'pos-auto-ajuda-' + opcao.campo"
                        switch
                        size="lg"
                        class="automacao-switch"
                      />
                    </div>
                  </section>
                </div>
              </details>

              <div class="d-flex flex-wrap justify-content-end mt-5">
                <b-button
                  type="button"
                  variant="outline-primary"
                  class="mr-2 mb-2"
                  :disabled="testandoConexao || salvandoConfiguracao"
                  @click="testarConexao"
                >
                  <b-spinner v-if="testandoConexao" small class="mr-2" />
                  Testar conexão
                </b-button>
                <b-button
                  type="submit"
                  variant="primary"
                  class="mb-2"
                  :disabled="salvandoConfiguracao || testandoConexao"
                >
                  <b-spinner v-if="salvandoConfiguracao" small class="mr-2" />
                  {{ configuracaoBotaoLabel }}
                </b-button>
              </div>
            </b-form>
          </b-overlay>
        </b-tab>

        <b-tab title="Catálogo" lazy>
          <b-overlay
            :show="
              carregandoCatalogo ||
              sincronizandoCatalogo ||
              baixandoCatalogo ||
              importandoBase
            "
            rounded="sm"
          >
            <b-alert v-if="!podeOperarCatalogo" show variant="warning">
              Salve as credenciais e ative a integração na aba Conexão para
              enviar ou receber dados.
            </b-alert>

            <div class="catalogo-envio p-5 mb-5">
              <span class="catalogo-direcao">Sistema → POS Controle</span>
              <h5 class="font-weight-bolder mt-2 mb-2">
                Enviar sua base de produtos
              </h5>
              <p class="text-muted mb-4">
                Já tem produtos no Sistema? Envie todos os produtos ativos e
                seus grupos para a API do POS, inclusive os que ainda não
                possuem vínculo.
              </p>
              <b-button
                variant="primary"
                data-testid="enviar-todos-produtos"
                :disabled="operacaoCatalogoIndisponivel"
                @click="sincronizarTudo"
              >
                <b-spinner v-if="sincronizandoCatalogo" small class="mr-2" />
                Enviar todos os produtos para o POS
              </b-button>
            </div>

            <b-alert
              v-if="catalogoResultado"
              show
              :variant="catalogoResultadoVariant"
            >
              <strong>{{ catalogoResultadoMensagem }}</strong>
              <ul v-if="catalogoErros.length" class="mb-0 mt-2">
                <li v-for="(erro, index) in catalogoErros" :key="index">
                  {{ erro }}
                </li>
              </ul>
            </b-alert>

            <b-alert
              v-if="catalogoDownloadResultado"
              show
              :variant="catalogoDownloadVariant"
            >
              <strong>{{ catalogoDownloadMensagem }}</strong>
              <ul v-if="catalogoDownloadErros.length" class="mb-0 mt-2">
                <li
                  v-for="(erro, index) in catalogoDownloadErros"
                  :key="'download-' + index"
                >
                  {{ erro }}
                </li>
              </ul>
            </b-alert>

            <details class="pos-section mb-4" @toggle="alternarSelecaoCatalogo">
              <summary>Enviar apenas alguns grupos ou produtos</summary>
              <div class="p-4">
                <div class="d-flex justify-content-end mb-4">
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    :disabled="
                      operacaoCatalogoIndisponivel || carregandoCatalogo
                    "
                    @click="carregarCatalogo"
                  >
                    Atualizar lista
                  </b-button>
                </div>
                <b-row>
                  <b-col lg="5" class="mb-5 mb-lg-0">
                    <div class="catalogo-panel border rounded">
                      <div class="p-4 border-bottom">
                        <div
                          class="d-flex justify-content-between align-items-center mb-3"
                        >
                          <strong>Grupos</strong>
                          <span class="text-muted"
                            >{{
                              gruposSelecionados.length
                            }}
                            selecionado(s)</span
                          >
                        </div>
                        <b-form-input
                          v-model.trim="buscaGrupo"
                          placeholder="Pesquisar grupo"
                          size="sm"
                        />
                        <b-form-checkbox
                          class="mt-3"
                          :checked="todosGruposVisiveisSelecionados"
                          @change="selecionarGruposVisiveis"
                        >
                          Selecionar os grupos exibidos
                        </b-form-checkbox>
                      </div>
                      <div class="catalogo-lista">
                        <label
                          v-for="grupo in gruposFiltrados"
                          :key="grupo.id"
                          class="catalogo-item"
                        >
                          <b-form-checkbox
                            v-model="gruposSelecionados"
                            :value="grupo.id"
                          />
                          <img
                            v-if="grupo.imagem"
                            :src="grupo.imagem"
                            :alt="grupo.nome"
                            class="catalogo-thumb"
                          />
                          <span class="ml-2">
                            <strong>{{ grupo.nome }}</strong>
                          </span>
                        </label>
                        <div v-if="!gruposFiltrados.length" class="empty-list">
                          Nenhum grupo encontrado.
                        </div>
                      </div>
                    </div>
                  </b-col>

                  <b-col lg="7">
                    <div class="catalogo-panel border rounded">
                      <div class="p-4 border-bottom">
                        <div
                          class="d-flex justify-content-between align-items-center mb-3"
                        >
                          <strong>Produtos ativos</strong>
                          <span class="text-muted">
                            {{ produtosSelecionados.length }} selecionado(s) ·
                            exibindo {{ produtosFiltrados.length }} de
                            {{ totalProdutosFiltrados }}
                          </span>
                        </div>
                        <b-form-input
                          v-model.trim="buscaProduto"
                          placeholder="Pesquisar por código ou descrição"
                          size="sm"
                        />
                        <b-form-checkbox
                          class="mt-3"
                          :checked="todosProdutosVisiveisSelecionados"
                          @change="selecionarProdutosVisiveis"
                        >
                          Selecionar os produtos exibidos
                        </b-form-checkbox>
                      </div>
                      <div class="catalogo-lista">
                        <label
                          v-for="produto in produtosFiltrados"
                          :key="produto.id"
                          class="catalogo-item"
                        >
                          <b-form-checkbox
                            v-model="produtosSelecionados"
                            :value="produto.id"
                          />
                          <img
                            v-if="produto.imagem"
                            :src="produto.imagem"
                            :alt="produto.descricao"
                            class="catalogo-thumb"
                          />
                          <span class="ml-2 flex-grow-1">
                            <strong
                              >{{ produto.codigo }} ·
                              {{ produto.descricao }}</strong
                            >
                            <small class="d-block text-muted">
                              {{ produto.grupo || "Sem grupo" }} ·
                              {{ formatarMoeda(produto.preco) }}
                            </small>
                          </span>
                        </label>
                        <div
                          v-if="!produtosFiltrados.length"
                          class="empty-list"
                        >
                          Nenhum produto encontrado.
                        </div>
                      </div>
                    </div>
                  </b-col>
                </b-row>

                <div class="d-flex justify-content-end mt-5">
                  <b-button
                    variant="success"
                    :disabled="
                      !possuiSelecaoCatalogo ||
                      operacaoCatalogoIndisponivel ||
                      carregandoCatalogo
                    "
                    @click="sincronizarSelecionados"
                  >
                    Enviar selecionados para o POS
                  </b-button>
                </div>
              </div>
            </details>

            <details class="pos-section">
              <summary>Receber dados do POS no Sistema</summary>
              <div class="p-4">
                <span class="catalogo-direcao">POS Controle → Sistema</span>
                <h6 class="font-weight-bold mt-3">
                  Atualizar produtos já vinculados
                </h6>
                <p class="text-muted">
                  Baixe alterações feitas no POS para atualizar os grupos e
                  produtos que já possuem vínculo no Sistema.
                </p>
                <b-button
                  variant="outline-secondary"
                  :disabled="operacaoCatalogoIndisponivel"
                  @click="baixarAlteracoesCatalogo"
                >
                  Baixar alterações do POS
                </b-button>
                <div class="border-top pt-4 mt-4">
                  <div
                    class="d-flex flex-wrap justify-content-between align-items-center"
                  >
                    <div class="pr-md-5">
                      <h5 class="font-weight-bolder mb-2">
                        Importar base do POS para o Sistema
                      </h5>
                      <p class="text-muted mb-0">
                        Use quando os dados já estão no POS: importa grupos,
                        produtos e vendas do mês atual para o Sistema.
                      </p>
                    </div>
                    <b-button
                      variant="outline-secondary"
                      class="mt-3 mt-md-0"
                      :disabled="operacaoCatalogoIndisponivel"
                      @click="confirmarImportacaoInicial"
                    >
                      <b-spinner v-if="importandoBase" small class="mr-2" />
                      Importar base do POS
                    </b-button>
                  </div>

                  <b-alert
                    v-if="importacaoInicialResultado"
                    show
                    :variant="importacaoInicialVariant"
                    class="mt-4 mb-0"
                  >
                    <strong>{{ importacaoInicialMensagem }}</strong>
                    <ul v-if="importacaoInicialErros.length" class="mb-0 mt-2">
                      <li
                        v-for="(erro, index) in importacaoInicialErros"
                        :key="'importacao-' + index"
                      >
                        {{ erro }}
                      </li>
                    </ul>
                  </b-alert>
                </div>
              </div>
            </details>
          </b-overlay>
        </b-tab>

        <b-tab title="Vendas" lazy>
          <b-overlay
            :show="carregandoVendas || sincronizandoVendas"
            rounded="sm"
          >
            <b-form @submit.prevent="aplicarFiltrosVendas">
              <b-row align-v="end">
                <b-col md="2">
                  <b-form-group label="Data inicial">
                    <b-form-input
                      v-model="filtrosVendas.data_inicial"
                      type="date"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="2">
                  <b-form-group label="Data final">
                    <b-form-input
                      v-model="filtrosVendas.data_final"
                      type="date"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="2">
                  <b-form-group label="Situação no POS">
                    <b-form-select v-model="filtrosVendas.status_remoto">
                      <option value="">Todas</option>
                      <option value="1">Concluída</option>
                      <option value="3">Cancelada</option>
                    </b-form-select>
                  </b-form-group>
                </b-col>
                <b-col md="2">
                  <b-form-group label="Terminal">
                    <b-form-input
                      v-model.trim="filtrosVendas.serial_number"
                      placeholder="Número serial"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="2">
                  <b-form-group label="Busca">
                    <b-form-input
                      v-model.trim="filtrosVendas.busca"
                      placeholder="Venda, NF ou cliente"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="2" class="mb-5">
                  <b-button type="submit" variant="primary" block>
                    Filtrar
                  </b-button>
                </b-col>
              </b-row>
            </b-form>

            <div
              class="d-flex flex-wrap justify-content-between align-items-center mb-4"
            >
              <span class="text-muted"
                >{{ paginacaoVendas.total }} venda(s) importada(s)</span
              >
              <b-button
                variant="success"
                :disabled="sincronizandoVendas"
                @click="sincronizarPeriodoVendas"
              >
                <b-spinner v-if="sincronizandoVendas" small class="mr-2" />
                Buscar novas vendas no POS
              </b-button>
            </div>

            <b-alert
              v-if="vendasResultado"
              show
              :variant="vendasResultadoVariant"
            >
              {{ vendasResultadoMensagem }}
            </b-alert>

            <div class="table-responsive">
              <table class="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Venda</th>
                    <th>Terminal</th>
                    <th>NF</th>
                    <th class="text-right">Total</th>
                    <th>Status</th>
                    <th class="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="venda in vendas" :key="venda.id">
                    <td>{{ formatarData(venda.vendida_em) }}</td>
                    <td>
                      <strong>{{
                        venda.sale_pos_code_id || venda.sale_id
                      }}</strong>
                      <small
                        v-if="documentoClienteVenda(venda)"
                        class="d-block text-muted"
                      >
                        Cliente: {{ documentoClienteVenda(venda) }}
                      </small>
                      <small v-if="venda.id_nfe" class="d-block text-success">
                        Documento Nuts #{{ venda.id_nfe }}
                      </small>
                    </td>
                    <td>{{ venda.serial_number || "—" }}</td>
                    <td>{{ venda.numero_nf || "—" }}</td>
                    <td class="text-right font-weight-bold">
                      {{ formatarMoeda(venda.valor_total) }}
                    </td>
                    <td>
                      <span class="badge" :class="statusVendaClass(venda)">
                        {{ statusVendaLabel(venda) }}
                      </span>
                      <small v-if="venda.ultimo_erro_nfce" class="d-block text-danger mt-1">
                        NFC-e: {{ venda.ultimo_erro_nfce }}
                      </small>
                    </td>
                    <td class="text-right">
                      <b-button
                        size="sm"
                        variant="outline-primary"
                        @click="abrirDetalheVenda(venda.id)"
                      >
                        Detalhes
                      </b-button>
                    </td>
                  </tr>
                  <tr v-if="!vendas.length">
                    <td colspan="7" class="text-center text-muted py-8">
                      Nenhuma venda encontrada. Use “Buscar novas vendas no POS”
                      para importar.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <b-pagination
              v-if="paginacaoVendas.total > paginacaoVendas.per_page"
              v-model="paginaVendas"
              :total-rows="paginacaoVendas.total"
              :per-page="paginacaoVendas.per_page"
              align="right"
              @change="mudarPaginaVendas"
            />
          </b-overlay>
        </b-tab>
      </b-tabs>
    </b-card>

    <b-modal
      id="pos-controle-venda-detalhe"
      size="xl"
      title="Detalhes da venda no POS"
      hide-footer
    >
      <b-overlay :show="carregandoDetalhe" rounded="sm">
        <div v-if="vendaDetalhe">
          <b-row class="mb-5">
            <b-col md="3"
              ><strong>Venda:</strong><br />{{ vendaDetalhe.sale_id }}</b-col
            >
            <b-col md="3"
              ><strong>Data:</strong><br />{{
                formatarData(vendaDetalhe.vendida_em)
              }}</b-col
            >
            <b-col md="3"
              ><strong>Terminal:</strong><br />{{
                vendaDetalhe.serial_number || "—"
              }}</b-col
            >
            <b-col md="3"
              ><strong>Total:</strong><br />{{
                formatarMoeda(vendaDetalhe.valor_total)
              }}</b-col
            >
          </b-row>

          <b-alert v-if="vendaDetalhe.ultimo_erro_nfce" show variant="danger">
            Falha na emissão automática de NFC-e: {{ vendaDetalhe.ultimo_erro_nfce }}
          </b-alert>
          <p v-if="vendaDetalhe.ultima_tentativa_nfce_em" class="text-muted">
            Última tentativa de emissão automática:
            {{ formatarData(vendaDetalhe.ultima_tentativa_nfce_em) }}
          </p>

          <h6 class="font-weight-bolder">Itens</h6>
          <div class="table-responsive mb-5">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Produto</th>
                  <th class="text-right">Qtd.</th>
                  <th class="text-right">Unitário</th>
                  <th class="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in itensVendaDetalhe" :key="index">
                  <td>{{ nomeItemVenda(item) }}</td>
                  <td class="text-right">
                    {{ item.Quantity || item.quantity || "—" }}
                  </td>
                  <td class="text-right">
                    {{ formatarMoeda(item.UnitPrice || item.PriceAmount || 0) }}
                  </td>
                  <td class="text-right">
                    {{ formatarMoeda(item.AmntTotal || totalItemVenda(item)) }}
                  </td>
                </tr>
                <tr v-if="!itensVendaDetalhe.length">
                  <td colspan="4" class="text-muted text-center">
                    A API não retornou os itens desta venda.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h6 class="font-weight-bolder">Pagamentos</h6>
          <div class="table-responsive">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Forma</th>
                  <th>Autorização</th>
                  <th class="text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(pagamento, index) in pagamentosVendaDetalhe"
                  :key="index"
                >
                  <td>
                    {{
                      pagamento.PaymentTypeName ||
                      pagamento.Name ||
                      pagamento.PaymentType ||
                      "—"
                    }}
                  </td>
                  <td>
                    {{
                      pagamento.AuthorizationCode ||
                      pagamento.Authorization ||
                      "—"
                    }}
                  </td>
                  <td class="text-right">
                    {{
                      formatarMoeda(
                        pagamento.PaidAmount || pagamento.Amount || 0
                      )
                    }}
                  </td>
                </tr>
                <tr v-if="!pagamentosVendaDetalhe.length">
                  <td colspan="3" class="text-muted text-center">
                    A API não retornou os pagamentos desta venda.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import PosControleService from "@/modules/PosControle/services/PosControleService";

const dataLocal = (data) => {
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, "0");
  const dia = String(data.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
};
const hoje = () => dataLocal(new Date());
const diasAtras = (dias) => {
  const data = new Date();
  data.setDate(data.getDate() - dias);
  return dataLocal(data);
};

const automacoes = [
  {
    titulo: "Sincronização automática",
    descricao: "Escolha o que atualizar entre o sistema e o POS.",
    icone: "fas fa-sync-alt",
    opcoes: [
      {
        campo: "sincronizar_grupos",
        titulo: "Atualizar grupos automaticamente",
        descricao: "Envia ao POS as alterações no cadastro de grupos.",
      },
      {
        campo: "sincronizar_produtos",
        titulo: "Atualizar produtos automaticamente",
        descricao: "Envia ao POS as alterações no cadastro de produtos.",
      },
      {
        campo: "sincronizar_vendas",
        titulo: "Buscar vendas automaticamente",
        descricao: "Importa para o sistema as novas vendas realizadas no POS.",
      },
    ],
  },
  {
    titulo: "Ao receber vendas do POS",
    descricao: "Defina como as vendas serão tratadas no sistema.",
    icone: "fas fa-receipt",
    opcoes: [
      {
        campo: "emitir_nfce_automaticamente",
        titulo: "Emitir NFC-e automaticamente",
        descricao:
          "A cada 10 minutos, emite as pré-vendas com a configuração padrão de NFC-e. Pendências fiscais aguardam correção.",
      },
      {
        campo: "movimentar_estoque",
        titulo: "Baixar estoque ao importar as vendas",
        descricao: "Atualiza o estoque dos produtos vinculados à venda.",
      },
      {
        campo: "gerar_financeiro",
        titulo: "Gerar financeiro conforme o pagamento",
        descricao:
          "Registra os recebimentos nas formas de pagamento cadastradas.",
      },
    ],
  },
];

export default {
  name: "app-pos-controle",
  data() {
    return {
      automacoes,
      abaAtiva: 0,
      carregandoConfiguracao: false,
      salvandoConfiguracao: false,
      testandoConexao: false,
      importandoBase: false,
      importacaoInicialResultado: null,
      mostrarSenha: false,
      mostrarChaveOcp: false,
      configurado: false,
      possuiSenha: false,
      possuiChaveOcp: false,
      origemCredenciais: null,
      integracao: {},
      configForm: {
        username: "",
        password: "",
        chave_ocp: "",
        ativo: true,
        sincronizar_grupos: false,
        sincronizar_produtos: false,
        sincronizar_vendas: false,
        emitir_nfce_automaticamente: false,
        movimentar_estoque: false,
        gerar_financeiro: false,
      },
      carregandoCatalogo: false,
      catalogoCarregado: false,
      sincronizandoCatalogo: false,
      grupos: [],
      produtos: [],
      gruposSelecionados: [],
      produtosSelecionados: [],
      buscaGrupo: "",
      buscaProduto: "",
      catalogoResultado: null,
      baixandoCatalogo: false,
      catalogoDownloadResultado: null,
      carregandoVendas: false,
      vendasCarregadas: false,
      sincronizandoVendas: false,
      vendas: [],
      paginaVendas: 1,
      paginacaoVendas: { total: 0, per_page: 25, last_page: 1 },
      filtrosVendas: {
        data_inicial: diasAtras(7),
        data_final: hoje(),
        status_remoto: "",
        serial_number: "",
        busca: "",
      },
      vendasResultado: null,
      carregandoDetalhe: false,
      vendaDetalhe: null,
    };
  },
  computed: {
    ...mapGetters(["itsblocked"]),
    statusConexaoClass() {
      if (!this.configurado) return "badge-light-warning";
      return this.configForm.ativo ? "badge-success" : "badge-light-secondary";
    },
    statusConexaoLabel() {
      if (!this.configurado) return "Não configurado";
      return this.configForm.ativo
        ? "Configurado e ativo"
        : "Integração inativa";
    },
    podeOperarCatalogo() {
      return (
        this.configurado &&
        this.integracao.ativo !== false &&
        !this.carregandoConfiguracao
      );
    },
    operacaoCatalogoIndisponivel() {
      return (
        !this.podeOperarCatalogo ||
        this.sincronizandoCatalogo ||
        this.baixandoCatalogo ||
        this.importandoBase ||
        this.salvandoConfiguracao
      );
    },
    configuracaoBotaoLabel() {
      return this.configurado ? "Atualizar dados" : "Salvar configuração";
    },
    origemCredenciaisLabel() {
      if (this.origemCredenciais === "integracao")
        return "salvas nesta integração";
      if (this.origemCredenciais === "configuracao_pos")
        return "reaproveitadas do terminal POS";
      return "ainda não informadas";
    },
    descricaoSenha() {
      return this.possuiSenha
        ? "Já existe uma senha salva. Deixe vazio para mantê-la."
        : "Informe uma senha para configurar a conexão com o POS Controle.";
    },
    descricaoChaveOcp() {
      return this.possuiChaveOcp
        ? "Já existe uma CHAVE OCP salva. Deixe vazio para mantê-la."
        : "Obrigatória para autenticar as requisições na API do POS Controle.";
    },
    importacaoInicialErros() {
      if (!this.importacaoInicialResultado) return [];
      const catalogo = this.importacaoInicialResultado.catalogo || {};
      const grupos = catalogo.grupos?.erros || [];
      const produtos = catalogo.produtos?.erros || [];
      const vendas = this.importacaoInicialResultado.vendas?.erros || [];
      return grupos
        .map(
          (item) =>
            `Grupo ${item.nome ? `"${item.nome}"` : item.id_remoto}: ${
              item.erro
            }`
        )
        .concat(
          produtos.map(
            (item) =>
              `Produto ${item.nome ? `"${item.nome}"` : item.id_remoto}: ${
                item.erro
              }`
          ),
          vendas.map(
            (item) => `Venda ${item.sale_id || "sem ID"}: ${item.erro}`
          )
        );
    },
    importacaoInicialVariant() {
      return this.importacaoInicialErros.length ? "warning" : "success";
    },
    importacaoInicialMensagem() {
      if (!this.importacaoInicialResultado) return "";
      const catalogo = this.importacaoInicialResultado.catalogo || {};
      const grupos = catalogo.grupos || {};
      const produtos = catalogo.produtos || {};
      const vendas = this.importacaoInicialResultado.vendas || {};
      return `${grupos.criados || 0} grupo(s) e ${
        produtos.criados || 0
      } produto(s) criado(s); ${grupos.atualizados || 0} grupo(s) e ${
        produtos.atualizados || 0
      } produto(s) atualizado(s); ${
        vendas.processadas || 0
      } venda(s) do mês processada(s).`;
    },
    gruposFiltrados() {
      const busca = this.normalizar(this.buscaGrupo);
      if (!busca) return this.grupos;
      return this.grupos.filter((grupo) =>
        this.normalizar(grupo.nome).includes(busca)
      );
    },
    produtosFiltrados() {
      const busca = this.normalizar(this.buscaProduto);
      const produtos = busca
        ? this.produtos.filter((produto) =>
            this.normalizar(
              `${produto.codigo} ${produto.descricao} ${produto.grupo || ""}`
            ).includes(busca)
          )
        : this.produtos;

      return produtos.slice(0, 300);
    },
    totalProdutosFiltrados() {
      const busca = this.normalizar(this.buscaProduto);
      if (!busca) return this.produtos.length;
      return this.produtos.filter((produto) =>
        this.normalizar(
          `${produto.codigo} ${produto.descricao} ${produto.grupo || ""}`
        ).includes(busca)
      ).length;
    },
    todosGruposVisiveisSelecionados() {
      return (
        this.gruposFiltrados.length > 0 &&
        this.gruposFiltrados.every((item) =>
          this.gruposSelecionados.includes(item.id)
        )
      );
    },
    todosProdutosVisiveisSelecionados() {
      return (
        this.produtosFiltrados.length > 0 &&
        this.produtosFiltrados.every((item) =>
          this.produtosSelecionados.includes(item.id)
        )
      );
    },
    possuiSelecaoCatalogo() {
      return (
        this.gruposSelecionados.length > 0 ||
        this.produtosSelecionados.length > 0
      );
    },
    catalogoErros() {
      if (!this.catalogoResultado) return [];
      const grupos =
        (this.catalogoResultado.grupos &&
          this.catalogoResultado.grupos.erros) ||
        [];
      const produtos =
        (this.catalogoResultado.produtos &&
          this.catalogoResultado.produtos.erros) ||
        [];
      return grupos
        .map((item) => {
          const grupo = this.grupos.find(
            (registro) => Number(registro.id) === Number(item.id)
          );
          return `Grupo ${grupo ? `"${grupo.nome}"` : `#${item.id}`}: ${
            item.erro
          }`;
        })
        .concat(
          produtos.map((item) => {
            const produto = this.produtos.find(
              (registro) => Number(registro.id) === Number(item.id)
            );
            return `Produto ${
              produto ? `"${produto.descricao}"` : `#${item.id}`
            }: ${item.erro}`;
          })
        );
    },
    catalogoTotalSincronizados() {
      if (!this.catalogoResultado) return 0;
      return (
        Number(this.catalogoResultado.grupos?.sincronizados || 0) +
        Number(this.catalogoResultado.produtos?.sincronizados || 0)
      );
    },
    catalogoResultadoVariant() {
      if (!this.catalogoErros.length) return "success";
      return this.catalogoTotalSincronizados ? "warning" : "danger";
    },
    catalogoResultadoMensagem() {
      const grupos = this.catalogoResultado && this.catalogoResultado.grupos;
      const produtos =
        this.catalogoResultado && this.catalogoResultado.produtos;
      const resumo = `${(grupos && grupos.sincronizados) || 0} grupo(s) e ${
        (produtos && produtos.sincronizados) || 0
      } produto(s) sincronizado(s).`;
      if (!this.catalogoErros.length) return resumo;
      if (!this.catalogoTotalSincronizados)
        return "Falha na sincronização: nenhum item foi enviado ao POS Controle.";
      return `Sincronização parcial. ${resumo}`;
    },
    catalogoDownloadErros() {
      if (!this.catalogoDownloadResultado) return [];
      const grupos = this.catalogoDownloadResultado.grupos?.erros || [];
      const produtos = this.catalogoDownloadResultado.produtos?.erros || [];
      return grupos
        .map((item) => "Grupo #" + item.id + ": " + item.erro)
        .concat(
          produtos.map((item) => "Produto #" + item.id + ": " + item.erro)
        );
    },
    catalogoDownloadTotalAtualizados() {
      if (!this.catalogoDownloadResultado) return 0;
      return (
        Number(this.catalogoDownloadResultado.grupos?.atualizados || 0) +
        Number(this.catalogoDownloadResultado.produtos?.atualizados || 0)
      );
    },
    catalogoDownloadVariant() {
      if (!this.catalogoDownloadErros.length) return "success";
      return this.catalogoDownloadTotalAtualizados ? "warning" : "danger";
    },
    catalogoDownloadMensagem() {
      if (!this.catalogoDownloadResultado) return "";
      const grupos = this.catalogoDownloadResultado.grupos || {};
      const produtos = this.catalogoDownloadResultado.produtos || {};
      const semAlteracao =
        Number(grupos.sem_alteracao || 0) + Number(produtos.sem_alteracao || 0);
      const naoMapeados =
        Number(grupos.nao_mapeados || 0) + Number(produtos.nao_mapeados || 0);

      return (
        (grupos.atualizados || 0) +
        " grupo(s) e " +
        (produtos.atualizados || 0) +
        " produto(s) atualizados no Sistema; " +
        semAlteracao +
        " sem alteração e " +
        naoMapeados +
        " item(ns) do POS sem vínculo ignorado(s)."
      );
    },
    vendasResultadoVariant() {
      if (!this.vendasResultado) return "success";
      if (Number(this.vendasResultado.pendentes || 0) > 0) return "warning";
      return this.vendasResultado.erros && this.vendasResultado.erros.length
        ? "warning"
        : "success";
    },
    vendasResultadoMensagem() {
      if (!this.vendasResultado) return "";
      return `${this.vendasResultado.consultadas || 0} consultada(s), ${
        this.vendasResultado.criadas || 0
      } nova(s), ${this.vendasResultado.atualizadas || 0} atualizada(s) e ${
        this.vendasResultado.canceladas || 0
      } cancelada(s); ${
        this.vendasResultado.processadas || 0
      } documento(s) Nuts processado(s) e ${
        this.vendasResultado.pendentes || 0
      } pendente(s).`;
    },
    itensVendaDetalhe() {
      const payload = (this.vendaDetalhe && this.vendaDetalhe.payload) || {};
      return payload.SaleItems || payload.saleItems || payload.Items || [];
    },
    pagamentosVendaDetalhe() {
      const payload = (this.vendaDetalhe && this.vendaDetalhe.payload) || {};
      return (
        payload.SalePaymentType ||
        payload.salePaymentType ||
        payload.Payments ||
        []
      );
    },
  },
  mounted() {
    if (this.itsblocked) {
      this.$router.push({ name: "assinatura" });
      return;
    }
    const aba = this.$route.query.aba;
    this.abaAtiva = aba === "catalogo" ? 1 : aba === "vendas" ? 2 : 0;
    this.carregarConfiguracao();
    if (this.abaAtiva === 2) this.buscarVendas();
  },
  methods: {
    alterarAba(index) {
      const aba = ["conexao", "catalogo", "vendas"][index] || "conexao";
      if (this.$route.query.aba !== aba) {
        this.$router
          .replace({ query: { ...this.$route.query, aba } })
          .catch(() => {});
      }
      if (index === 2 && !this.vendasCarregadas) this.buscarVendas();
    },
    async carregarConfiguracao() {
      this.carregandoConfiguracao = true;
      try {
        const response = await PosControleService.configuracao();
        const resumo = response.data.data || {};
        this.configurado = !!resumo.configurado;
        this.possuiSenha = !!resumo.possui_senha;
        this.possuiChaveOcp = !!resumo.possui_chave_ocp;
        this.origemCredenciais = resumo.origem_credenciais || null;
        this.integracao = resumo.integracao || {};
        this.configForm.ativo = this.integracao.ativo !== false;
        this.configForm.sincronizar_grupos =
          !!this.integracao.sincronizar_grupos;
        this.configForm.sincronizar_produtos =
          !!this.integracao.sincronizar_produtos;
        this.configForm.sincronizar_vendas =
          !!this.integracao.sincronizar_vendas;
        this.configForm.movimentar_estoque =
          !!this.integracao.movimentar_estoque;
        this.configForm.gerar_financeiro = !!this.integracao.gerar_financeiro;
        this.configForm.emitir_nfce_automaticamente =
          !!this.integracao.emitir_nfce_automaticamente;
      } finally {
        this.carregandoConfiguracao = false;
      }
    },
    async salvarConfiguracao() {
      if (!this.possuiChaveOcp && !this.configForm.chave_ocp) {
        this.$toast.warning("Informe a CHAVE OCP da integração POS Controle.");
        return;
      }

      this.salvandoConfiguracao = true;
      try {
        const payload = {
          ativo: this.configForm.ativo,
          sincronizar_grupos: this.configForm.sincronizar_grupos,
          sincronizar_produtos: this.configForm.sincronizar_produtos,
          sincronizar_vendas: this.configForm.sincronizar_vendas,
          movimentar_estoque: this.configForm.movimentar_estoque,
          gerar_financeiro: this.configForm.gerar_financeiro,
          emitir_nfce_automaticamente: this.configForm.emitir_nfce_automaticamente,
        };
        if (this.configForm.username)
          payload.username = this.configForm.username;
        if (this.configForm.password)
          payload.password = this.configForm.password;
        if (this.configForm.chave_ocp)
          payload.chave_ocp = this.configForm.chave_ocp;
        await PosControleService.salvarConfiguracao(payload);
        this.configForm.password = "";
        this.configForm.chave_ocp = "";
        this.$toast.success("Configuração do POS Controle salva.");
        await this.carregarConfiguracao();
      } finally {
        this.salvandoConfiguracao = false;
      }
    },
    async testarConexao() {
      this.testandoConexao = true;
      try {
        await PosControleService.testarConexao();
        this.$toast.success(
          "Conexão com o POS Controle realizada com sucesso."
        );
        this.configurado = true;
      } finally {
        this.testandoConexao = false;
      }
    },
    confirmarImportacaoInicial() {
      return this.$swal
        .fire({
          icon: "warning",
          title: "Importar a base do POS Controle?",
          text: "Serão criados ou atualizados grupos e produtos no Sistema e importadas as vendas do mês atual. As opções de estoque e financeiro configuradas serão aplicadas.",
          showCancelButton: true,
          confirmButtonText: "Importar agora",
          cancelButtonText: "Cancelar",
        })
        .then((result) => {
          if (result.isConfirmed) return this.executarImportacaoInicial();
          return null;
        });
    },
    async executarImportacaoInicial() {
      this.importandoBase = true;
      this.importacaoInicialResultado = null;
      try {
        const response = await PosControleService.importarBaseInicial();
        this.importacaoInicialResultado = response.data.data;
        if (this.importacaoInicialErros.length) {
          this.$toast.warning("Importação inicial concluída com pendências.");
        } else {
          this.$toast.success("Base do POS Controle importada com sucesso.");
        }
        await this.carregarConfiguracao();
      } finally {
        this.importandoBase = false;
      }
    },
    alternarSelecaoCatalogo(event) {
      if (
        event.target.open &&
        !this.catalogoCarregado &&
        !this.carregandoCatalogo
      ) {
        return this.carregarCatalogo();
      }
    },
    async carregarCatalogo() {
      this.carregandoCatalogo = true;
      try {
        const [gruposResponse, produtosResponse] = await Promise.all([
          PosControleService.listarGrupos(),
          PosControleService.listarProdutos(),
        ]);
        const gruposPayload = gruposResponse.data.data || gruposResponse.data;
        const produtosPayload =
          produtosResponse.data.data || produtosResponse.data;
        this.grupos = Array.isArray(gruposPayload)
          ? gruposPayload
          : gruposPayload.data || [];
        this.produtos = Array.isArray(produtosPayload)
          ? produtosPayload
          : produtosPayload.data || [];
        this.catalogoCarregado = true;
      } finally {
        this.carregandoCatalogo = false;
      }
    },
    selecionarGruposVisiveis(selecionar) {
      const ids = this.gruposFiltrados.map((item) => item.id);
      this.gruposSelecionados = selecionar
        ? Array.from(new Set(this.gruposSelecionados.concat(ids)))
        : this.gruposSelecionados.filter((id) => !ids.includes(id));
    },
    selecionarProdutosVisiveis(selecionar) {
      const ids = this.produtosFiltrados.map((item) => item.id);
      this.produtosSelecionados = selecionar
        ? Array.from(new Set(this.produtosSelecionados.concat(ids)))
        : this.produtosSelecionados.filter((id) => !ids.includes(id));
    },
    sincronizarSelecionados() {
      return this.executarSincronizacaoCatalogo({
        grupo_ids: this.gruposSelecionados,
        produto_ids: this.produtosSelecionados,
      });
    },
    sincronizarTudo() {
      if (this.operacaoCatalogoIndisponivel) return;
      return this.$swal
        .fire({
          icon: "question",
          title: "Enviar todos os produtos para o POS?",
          text: "Todos os produtos ativos e grupos desta empresa serão enviados do Sistema para o POS Controle. Produtos já vinculados serão atualizados e os demais serão cadastrados. O tempo depende do tamanho da base.",
          showCancelButton: true,
          confirmButtonText: "Enviar todos os produtos",
          cancelButtonText: "Cancelar",
        })
        .then((result) => {
          if (result.isConfirmed && !this.operacaoCatalogoIndisponivel)
            return this.executarSincronizacaoCatalogo({ todos: true });
          return null;
        });
    },
    async executarSincronizacaoCatalogo(payload) {
      this.sincronizandoCatalogo = true;
      this.catalogoResultado = null;
      try {
        const response = await PosControleService.sincronizarCatalogo(payload);
        this.catalogoResultado = response.data.data;
        if (this.catalogoErros.length && !this.catalogoTotalSincronizados)
          this.$toast.error(
            "A sincronização falhou. Consulte o motivo destacado na tela."
          );
        else if (this.catalogoErros.length)
          this.$toast.warning("Sincronização concluída com pendências.");
        else this.$toast.success("Catálogo sincronizado com sucesso.");
      } finally {
        this.sincronizandoCatalogo = false;
      }
    },
    baixarAlteracoesCatalogo() {
      return this.$swal
        .fire({
          icon: "question",
          title: "Baixar alterações do POS Controle?",
          text: "Os dados do POS substituirão nome dos grupos e nome, código interno, preço, código de barras, status e grupo dos produtos já vinculados.",
          showCancelButton: true,
          confirmButtonText: "Baixar e atualizar Sistema",
          cancelButtonText: "Cancelar",
        })
        .then((result) => {
          if (result.isConfirmed) return this.executarDownloadCatalogo();
          return null;
        });
    },
    async executarDownloadCatalogo() {
      this.baixandoCatalogo = true;
      this.catalogoDownloadResultado = null;
      try {
        const response = await PosControleService.baixarCatalogo();
        this.catalogoDownloadResultado = response.data.data;
        if (
          this.catalogoDownloadErros.length &&
          !this.catalogoDownloadTotalAtualizados
        ) {
          this.$toast.error(
            "Não foi possível baixar as alterações. Consulte os erros na tela."
          );
        } else if (this.catalogoDownloadErros.length) {
          this.$toast.warning("Alterações baixadas parcialmente.");
        } else {
          this.$toast.success("Alterações do POS aplicadas no Sistema.");
        }
        await this.carregarCatalogo();
      } finally {
        this.baixandoCatalogo = false;
      }
    },
    async buscarVendas() {
      this.carregandoVendas = true;
      try {
        const response = await PosControleService.listarVendas({
          ...this.filtrosVendas,
          page: this.paginaVendas,
          per_page: this.paginacaoVendas.per_page,
        });
        const pagina = response.data.data || {};
        this.vendas = pagina.data || [];
        this.paginacaoVendas = {
          total: Number(pagina.total || 0),
          per_page: Number(pagina.per_page || 25),
          last_page: Number(pagina.last_page || 1),
        };
        this.vendasCarregadas = true;
      } finally {
        this.carregandoVendas = false;
      }
    },
    aplicarFiltrosVendas() {
      this.paginaVendas = 1;
      return this.buscarVendas();
    },
    mudarPaginaVendas(pagina) {
      this.paginaVendas = pagina;
      this.$nextTick(() => this.buscarVendas());
    },
    async sincronizarPeriodoVendas() {
      this.sincronizandoVendas = true;
      this.vendasResultado = null;
      try {
        const response = await PosControleService.sincronizarVendas({
          data_inicial: this.filtrosVendas.data_inicial || null,
          data_final: this.filtrosVendas.data_final || null,
        });
        this.vendasResultado = response.data.data;
        this.paginaVendas = 1;
        await this.buscarVendas();
        this.$toast.success("Busca de vendas concluída.");
      } finally {
        this.sincronizandoVendas = false;
      }
    },
    async abrirDetalheVenda(id) {
      this.vendaDetalhe = null;
      this.carregandoDetalhe = true;
      this.$bvModal.show("pos-controle-venda-detalhe");
      try {
        const response = await PosControleService.detalharVenda(id);
        this.vendaDetalhe = response.data.data;
      } finally {
        this.carregandoDetalhe = false;
      }
    },
    normalizar(value) {
      return String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },
    documentoClienteVenda(venda) {
      const digitos = String(venda?.documento_cliente || "").replace(/\D/g, "");
      if (!digitos || /^0+$/.test(digitos)) return "";
      return [11, 14].includes(digitos.length) ? digitos : "";
    },
    formatarMoeda(value) {
      const numero = Number(String(value || 0).replace(",", "."));
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(Number.isFinite(numero) ? numero : 0);
    },
    formatarData(value) {
      if (!value) return "—";
      const data = new Date(value);
      if (Number.isNaN(data.getTime())) return value;
      return new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(data);
    },
    statusVendaLabel(venda) {
      if (
        Number(venda.status_remoto) === 3 ||
        venda.status_processamento === "cancelada"
      )
        return "Cancelada";
      if (venda.status_processamento === "erro") return "Erro";
      if (venda.status_processamento === "processada") return "Processada";
      return "Importada";
    },
    statusVendaClass(venda) {
      if (
        Number(venda.status_remoto) === 3 ||
        venda.status_processamento === "cancelada"
      )
        return "badge-light-danger";
      if (venda.status_processamento === "erro") return "badge-light-warning";
      if (venda.status_processamento === "processada")
        return "badge-light-success";
      return "badge-light-primary";
    },
    nomeItemVenda(item) {
      return (
        item.ProductName ||
        item.Name ||
        item.Description ||
        item.InternalCode ||
        "Produto"
      );
    },
    totalItemVenda(item) {
      return (
        Number(item.Quantity || 0) *
        Number(item.UnitPrice || item.PriceAmount || 0)
      );
    },
  },
};
</script>

<style scoped>
.automacoes-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;
  padding: 1.5rem;
}

.automacoes-panel {
  min-width: 0;
  padding: 1.5rem;
  border: 1px solid #e4e6ef;
  border-radius: 0.65rem;
  background: #fafbfc;
}

.automacoes-heading {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.automacoes-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 42px;
  height: 42px;
  border: 1px solid #e4e6ef;
  border-radius: 10px;
  background: #fff;
}

.automacoes-icon i {
  color: var(--primary-color);
  font-size: 18px;
}

.automacoes-heading h6 {
  margin: 0 0 0.35rem;
  font-size: 16px;
  font-weight: 600;
  color: #3f4254;
}

.automacoes-heading p,
.automacao-text p {
  margin: 0;
  color: #6c7293;
  font-size: 13px;
  line-height: 1.5;
}

.automacao-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  min-height: 104px;
  padding: 1.25rem 0;
}

.automacao-option + .automacao-option {
  border-top: 1px solid #e4e6ef;
}

.automacao-text {
  min-width: 0;
}

.automacao-text label {
  display: block;
  margin: 0 0 0.35rem;
  color: #3f4254;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  cursor: pointer;
}

.automacao-switch {
  flex: 0 0 44px;
  width: 44px;
  min-height: 24px;
  margin: 0;
  padding-left: 0;
}

.automacao-switch.custom-switch ::v-deep .custom-control-label {
  position: static;
  width: 44px;
  min-height: 24px;
  cursor: pointer;
}

.automacao-switch.custom-switch ::v-deep .custom-control-label::before {
  top: 0;
  left: 0;
  width: 44px;
  height: 24px;
  border-radius: 12px;
}

.automacao-switch.custom-switch ::v-deep .custom-control-label::after {
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
}

.automacao-switch.custom-switch ::v-deep .custom-control-input:checked ~ .custom-control-label::after {
  transform: translateX(20px);
}

@media (max-width: 991.98px) {
  .automacoes-grid {
    grid-template-columns: 1fr;
    padding: 1rem;
  }

  .automacoes-panel {
    padding: 1.25rem;
  }
}

.pos-section {
  border: 1px solid #e4e6ef;
  border-radius: 0.65rem;
  background: #fff;
}

.pos-section > summary {
  padding: 1.25rem;
  color: #3f4254;
  font-weight: 600;
  cursor: pointer;
}

.pos-section > summary:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

.pos-section[open] > summary {
  border-bottom: 1px solid #e4e6ef;
}

.catalogo-envio {
  border: 1px solid #d8dce6;
  border-left: 4px solid var(--primary-color);
  border-radius: 0.65rem;
  background: #f8fafc;
}

.catalogo-direcao {
  color: #5e6278;
  font-size: 0.85rem;
  font-weight: 600;
}

.pos-controle-page .badge {
  padding: 0.7rem 1rem;
  font-size: 0.85rem;
}

.pos-controle-tabs ::v-deep .card-header {
  padding: 1rem;
  background: #f3f6f9;
  border-bottom: 1px solid #e4e6ef;
}

.pos-controle-tabs ::v-deep .pos-controle-tabs-nav {
  gap: 0.75rem;
}

.pos-controle-tabs ::v-deep .pos-controle-tabs-nav .nav-item {
  min-width: 150px;
}

.pos-controle-tabs ::v-deep .pos-controle-tabs-nav .nav-link {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 0.9rem 1.5rem;
  color: #3f4254 !important;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  background: #fff;
  border: 1px solid #d8dce6;
  border-radius: 0.65rem;
  transition: color 0.2s ease, background-color 0.2s ease,
    border-color 0.2s ease, box-shadow 0.2s ease;
}

.pos-controle-tabs ::v-deep .pos-controle-tabs-nav .nav-link:hover {
  color: var(--primary-color) !important;
  background: #fff;
  border-color: var(--primary-color);
}

.pos-controle-tabs ::v-deep .pos-controle-tabs-nav .nav-link.active {
  color: #fff !important;
  background: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  box-shadow: 0 0.35rem 0.85rem rgba(54, 153, 255, 0.28);
}

.pos-controle-tabs ::v-deep .pos-controle-tabs-nav .nav-link:focus-visible {
  outline: 3px solid rgba(54, 153, 255, 0.28);
  outline-offset: 2px;
}

.catalogo-panel {
  background: #fff;
}

.catalogo-lista {
  max-height: 460px;
  overflow-y: auto;
}

.catalogo-item {
  display: flex;
  align-items: center;
  min-height: 64px;
  padding: 0.75rem 1rem;
  margin: 0;
  border-bottom: 1px solid #eef0f5;
  cursor: pointer;
}

.catalogo-item:hover {
  background: #f8f9fa;
}

.catalogo-thumb {
  width: 42px;
  height: 42px;
  margin-left: 0.75rem;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
  object-fit: cover;
}

.empty-list {
  padding: 2.5rem 1rem;
  color: #7e8299;
  text-align: center;
}

.table td,
.table th {
  vertical-align: middle;
}

@media (max-width: 575.98px) {
  .pos-controle-tabs ::v-deep .pos-controle-tabs-nav {
    flex-direction: column;
  }

  .pos-controle-tabs ::v-deep .pos-controle-tabs-nav .nav-item {
    width: 100%;
  }
}
</style>
